import * as React from 'react';

import { STARDOG_CONTEXT_LOCAL_IRI } from 'bones/constants/iris';
import { MimeType } from 'bones/constants/mimeTypes';
import { VoiceboxPanelDataContextValue } from 'bones/constants/voicebox/panel';
import {
  ColumnMetadata,
  DataSource,
  LocalGraph,
  Mapping,
  SchemaMetadata,
  TableMetadata,
  VirtualGraph,
} from 'bones/constants/voicebox/provenance';
import { VoiceboxSettingsContextValue } from 'bones/constants/voicebox/settings';
import { getLocalName } from 'bones/utils/getLocalName';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { useWithLoading } from 'bones/utils/hooks/useWithLoading';
import { makeStardogQueryName } from 'bones/utils/voicebox/stardogQuery';
import { useLocalGraphsFilter } from 'bones/utils/voicebox/useLocalGraphsFilter';
import {
  QueryArgs,
  ResultBindings,
  usePortalVoiceboxStardogQuery,
} from 'bones/utils/voicebox/usePortalVoiceboxStardogQuery';

type ArrayElement<T> = T extends Array<infer I> ? I : T;

const getVirtualProvenanceQueryArgs = (
  database: string,
  classIris: string[]
): QueryArgs<
  ResultBindings<{
    dataSource: string;
    dataSourcePlatform: string;
    dataSourceLabel: string;
    dataSourceDescription: string;
    virtualGraph: string;
    virtualGraphLabel: string;
    virtualGraphDescription: string;
    mapping: string;
    type: string;
    property: string;
    propertyDatatype: string;
    column: string;
    columnLabel: string;
    table: string;
    tableLabel: string;
    schema: string;
    schemaLabel: string;
  }>,
  {
    dataSourcesByIri: Record<string, DataSource>;
    schemasByIri: Record<string, SchemaMetadata>;
    tablesByIri: Record<string, TableMetadata>;
    columnsByIri: Record<string, ColumnMetadata>;
    graphsByType: Record<string, Record<string, VirtualGraph>>;
  }
> => ({
  query: `\
# ${makeStardogQueryName('Voicebox Virtual Provenance', database, ...classIris)}

PREFIX catalog: <tag:stardog:api:catalog:>
PREFIX r2rml: <http://www.w3.org/ns/r2rml#>
PREFIX dct: <http://purl.org/dc/terms/>

SELECT DISTINCT
  ?dataSource
  ?dataSourcePlatform
  ?dataSourceLabel
  ?dataSourceDescription

  ?virtualGraph
  ?virtualGraphLabel
  ?virtualGraphDescription

  ?mapping
  ?type

  ?property
  ?propertyDatatype

  ?column
  ?columnLabel

  ?table
  ?tableLabel

  ?schema
  ?schemaLabel
{
  # virtual graph must be associated to the provided stardog database
  { ?virtualGraph catalog:owningDatabase "${database}" }

  # TODO: virtual graph can be associated to all databases ("*")
  # UNION { ?virtualGraph catalog:owningDatabase "*"}

  # virtual graphs and mappings for data source, optional label and description
  ?virtualGraph catalog:connectsTo ?dataSource ;
    catalog:hasMapping | dct:conformsTo ?mapping .
  OPTIONAL { ?virtualGraph rdfs:label ?virtualGraphLabel }
  OPTIONAL { ?virtualGraph rdfs:comment ?virtualGraphDescription }

  # data source platform, optional label and description
  ?dataSource catalog:platform ?dataSourcePlatform .
  OPTIONAL { ?dataSource rdfs:label ?dataSourceLabel }
  OPTIONAL { ?dataSource rdfs:comment ?dataSourceDescription }

  # data source optionally has schemas, optional label
  OPTIONAL {
    ?dataSource catalog:hasCatalog/catalog:hasSchema|catalog:hasSchema ?schema
    OPTIONAL { ?schema rdfs:label ?schemaLabel }
  }

  # data source has tables, optional label
  ?dataSource (catalog:hasCatalog/catalog:hasSchema|catalog:hasSchema|catalog:hasCatalog)/catalog:hasTable ?table .
  OPTIONAL { ?table rdfs:label ?tableLabel }

  # table has columns, optional label
  ?table catalog:hasColumn ?column .
  OPTIONAL { ?column rdfs:label ?columnLabel }

  # types to find mappings for
  VALUES ?type { ${classIris.map((iri) => `<${iri}>`).join(' ')} }

  {
    # class mapping must be filtered by provided types
    ?mapping r2rml:predicateObjectMap [
      # r2rml:predicateMap -> r2rml:constant = rdf:type for class mapping
      r2rml:predicateMap/r2rml:constant rdf:type ;
      # r2rml:objectMap -> r2rml:constant for class mapping iri
      r2rml:objectMap/r2rml:constant ?type
    ] .

    ?mapping r2rml:subjectMap/catalog:mapsColumn ?column .
  } UNION {
    # property mapping must be filtered by provided types
    ?mapping r2rml:predicateObjectMap [
      # r2rml:predicateMap -> r2rml:constant = rdf:type for class mapping
      r2rml:predicateMap/r2rml:constant rdf:type ;
      # r2rml:objectMap -> r2rml:constant for class mapping iri
      r2rml:objectMap/r2rml:constant ?type
    ] .

    ?mapping r2rml:predicateObjectMap ?predObjMap .
    ?predObjMap
      # r2rml:predicateMap -> r2rml:constant for property iri
      r2rml:predicateMap/r2rml:constant ?property ;
      # r2rml:objectMap -> catalog:mapsColumn for property mapping
      r2rml:objectMap/catalog:mapsColumn ?column .

    OPTIONAL {
      # r2rml:objectMap -> r2rml:datatype for attribute mapping
      ?predObjMap r2rml:objectMap/r2rml:datatype ?propertyDatatype ;
    }
  }
}
ORDER BY ?dataSource ?virtualGraph ?mapping ?type ?property ?propertyDatatype
LIMIT 1000
`,
  queryResultsMime: MimeType.JSON_SPARQL_RESULTS,
  parseBody: async (body) => {
    const dataSourcesByIri: Record<string, DataSource> = {};
    const schemasByIri: Record<string, SchemaMetadata> = {};
    const tablesByIri: Record<string, TableMetadata> = {};
    const columnsByIri: Record<string, ColumnMetadata> = {};
    const mappingsByIri: Record<string, Mapping> = {};
    const graphsByType: Record<string, Record<string, VirtualGraph>> = {};

    const addMetadataFromBinding =
      (
        binding: NonNullable<
          ArrayElement<NonNullable<typeof body.results>['bindings']>
        >
      ) =>
      <T, K extends keyof T>(
        metadata: Pick<{ [key in keyof T]: string }, K>,
        metadataKey: K,
        bindingKey: keyof typeof binding
      ) => {
        const value = binding[bindingKey]?.value;
        if (value) {
          // eslint-disable-next-line no-param-reassign
          metadata[metadataKey] = value;
        }
      };

    const bindings = body?.results?.bindings || [];
    bindings.forEach((binding) => {
      const addMetadata = addMetadataFromBinding(binding);

      const virtualGraphIri = binding.virtualGraph?.value;
      const dataSourceIri = binding.dataSource?.value;
      const dataSourcePlatformIri = binding.dataSourcePlatform?.value;
      const tableIri = binding.table?.value;
      const columnIri = binding.column?.value;
      const typeIri = binding.type?.value;
      const mappingIri = binding.mapping?.value;

      if (
        !virtualGraphIri ||
        !dataSourceIri ||
        !dataSourcePlatformIri ||
        !tableIri ||
        !columnIri ||
        !typeIri ||
        !mappingIri
      ) {
        // ignore results missing required fields
        return;
      }

      if (!dataSourcesByIri[dataSourceIri]) {
        dataSourcesByIri[dataSourceIri] = {
          iri: dataSourceIri,
          platform: dataSourcePlatformIri,
          name: getLocalName(dataSourceIri),
        };
      }

      const dataSource = dataSourcesByIri[dataSourceIri];
      addMetadata(dataSource, 'name', 'dataSourceLabel');
      addMetadata(dataSource, 'description', 'dataSourceDescription');

      const schemaIri = binding.schema?.value;
      if (schemaIri) {
        if (!schemasByIri[schemaIri]) {
          schemasByIri[schemaIri] = {
            iri: schemaIri,
            name: getLocalName(schemaIri),
          };
        }

        const schema = schemasByIri[schemaIri];
        addMetadata(schema, 'name', 'schemaLabel');
      }

      if (!tablesByIri[tableIri]) {
        tablesByIri[tableIri] = {
          iri: tableIri,
          name: getLocalName(tableIri),
          schemaIri,
        };
      }

      const table = tablesByIri[tableIri];
      addMetadata(table, 'name', 'tableLabel');

      if (!columnsByIri[columnIri]) {
        columnsByIri[columnIri] = {
          iri: columnIri,
          name: getLocalName(columnIri),
          tableIri,
        };
      }

      const column = columnsByIri[columnIri];
      addMetadata(column, 'name', 'columnLabel');

      if (!mappingsByIri[mappingIri]) {
        mappingsByIri[mappingIri] = {
          iri: mappingIri,
          toType: typeIri,
          typeMappings: [],
          propertyMappings: [],
        };
      }

      const mapping = mappingsByIri[mappingIri];
      if (binding.property?.value) {
        mapping.propertyMappings.push({
          columnIri,
          property: binding.property.value,
          datatype: binding.propertyDatatype?.value,
        });
      } else {
        mapping.typeMappings.push({
          columnIri,
        });
      }

      if (!graphsByType[typeIri]) {
        graphsByType[typeIri] = {};
      }

      const graphsByIri = graphsByType[typeIri];

      if (!graphsByIri[virtualGraphIri]) {
        graphsByIri[virtualGraphIri] = {
          iri: virtualGraphIri,
          dataSourceIri,
          database,
          mappings: [],
        };
      }

      const graph = graphsByIri[virtualGraphIri];
      addMetadata(graph, 'name', 'virtualGraphLabel');
      addMetadata(graph, 'description', 'virtualGraphDescription');

      if (!graph.mappings.includes(mapping)) {
        graph.mappings.push(mapping);
      }
    });

    return {
      dataSourcesByIri,
      schemasByIri,
      tablesByIri,
      columnsByIri,
      graphsByType,
    };
  },
});

const getLocalProvenanceQueryArgs = (
  classIris: string[]
): QueryArgs<
  ResultBindings<{
    graph: string;
    graphLabel: string;
    graphDescription: string;
    type: string;
  }>,
  Record<string, Record<string, LocalGraph>>
> => ({
  query: `\
# ${makeStardogQueryName('Voicebox Local Provenance', ...classIris)}

SELECT DISTINCT
  ?graph
  ?graphLabel
  ?graphDescription
  ?type
FROM NAMED <${STARDOG_CONTEXT_LOCAL_IRI}>
{
  # types to find graphs for
  {
    SELECT DISTINCT ?graph ?type {
      GRAPH ?graph {
        VALUES ?type { ${classIris.map((iri) => `<${iri}>`).join(' ')} }
        [] a ?type
      }
    }
  }

  # graph has optional label and description
  OPTIONAL { GRAPH ?graph { ?graph rdfs:label ?graphLabel } }
  OPTIONAL { GRAPH ?graph { ?graph rdfs:comment ?graphDescription } }
}
LIMIT 1000
`,
  queryResultsMime: MimeType.JSON_SPARQL_RESULTS,
  parseBody: async (body) => {
    const graphsByType: Record<string, Record<string, LocalGraph>> = {};

    const addMetadataFromBinding =
      (
        binding: NonNullable<
          ArrayElement<NonNullable<typeof body.results>['bindings']>
        >
      ) =>
      <T, K extends keyof T>(
        metadata: Pick<{ [key in keyof T]: string }, K>,
        metadataKey: K,
        bindingKey: keyof typeof binding
      ) => {
        const value = binding[bindingKey]?.value;
        if (value) {
          // eslint-disable-next-line no-param-reassign
          metadata[metadataKey] = value;
        }
      };

    const bindings = body?.results?.bindings || [];
    bindings.forEach((binding) => {
      const addMetadata = addMetadataFromBinding(binding);

      const graphIri = binding.graph?.value;
      const typeIri = binding.type?.value;

      if (!graphIri || !typeIri) {
        // ignore results missing required fields
        return;
      }

      if (!graphsByType[typeIri]) {
        graphsByType[typeIri] = {};
      }

      const graphsByIri = graphsByType[typeIri];

      if (!graphsByIri[graphIri]) {
        graphsByIri[graphIri] = {
          iri: graphIri,
          types: [],
        };
      }

      const graph = graphsByIri[graphIri];
      addMetadata(graph, 'name', 'graphLabel');
      addMetadata(graph, 'description', 'graphDescription');

      if (!graph.types.includes(typeIri)) {
        graph.types.push(typeIri);
      }
    });

    return graphsByType;
  },
});

export const usePortalVoiceboxProvenanceFetcher = (
  connectionIdx: number,
  catalogDatabaseId: string | undefined,
  settingsContext: VoiceboxSettingsContextValue,
  provenance: VoiceboxPanelDataContextValue['provenance'],
  setProvenance: (
    provenance: React.SetStateAction<
      VoiceboxPanelDataContextValue['provenance']
    >
  ) => void
) => {
  const isMounted = useIsMounted();

  const withLoading = useWithLoading('usePortalVoiceboxProvenanceFetcher');

  const {
    databasesById,
    namedGraphsByDatabaseId,
    settings: { databaseId, namedGraphs },
  } = settingsContext;

  const fetchVirtualProvenance = usePortalVoiceboxStardogQuery(
    connectionIdx,
    settingsContext,
    getVirtualProvenanceQueryArgs,
    {
      databaseId: catalogDatabaseId,
      requestOptions: { 'default-graph-uri': [STARDOG_CONTEXT_LOCAL_IRI] },
    }
  );

  const fetchLocalProvenance = usePortalVoiceboxStardogQuery(
    connectionIdx,
    settingsContext,
    getLocalProvenanceQueryArgs,
    { databaseId, requestOptions: {} }
  );

  const filteredGraphs = useLocalGraphsFilter(
    databaseId,
    databasesById,
    namedGraphs,
    namedGraphsByDatabaseId
  );

  return React.useCallback(
    async (iris: string[]) => {
      const missingIris: string[] = [];
      iris.forEach((iri) => {
        if (!provenance.graphsByType[iri]) {
          missingIris.push(iri);
        }
      });

      if (!missingIris.length) {
        return null;
      }

      return withLoading(
        (loading) => setProvenance((prev) => ({ ...prev, loading })),
        async () => {
          const [virtualProvenance, localProvenance] = await Promise.all([
            fetchVirtualProvenance(databaseId, missingIris),
            fetchLocalProvenance(missingIris),
          ]);

          if (!isMounted.current) {
            return;
          }

          setProvenance((prev) => {
            const graphsByType = { ...prev.graphsByType };

            Object.entries(virtualProvenance?.graphsByType || {}).forEach(
              ([typeIri, virtualGraphs]: [
                string,
                Record<string, LocalGraph | VirtualGraph>
              ]) => {
                const graphs = Object.values(virtualGraphs);
                if (!graphs.length) {
                  return;
                }

                if (!graphsByType[typeIri]) {
                  graphsByType[typeIri] = [];
                }
                graphsByType[typeIri].push(...graphs);
              }
            );

            Object.entries(localProvenance || {}).forEach(
              ([typeIri, localGraphs]: [
                string,
                Record<string, LocalGraph | VirtualGraph>
              ]) => {
                const graphs = Object.values(localGraphs).filter((localGraph) =>
                  filteredGraphs.has(localGraph.iri)
                );
                if (!graphs.length) {
                  return;
                }

                if (!graphsByType[typeIri]) {
                  graphsByType[typeIri] = [];
                }
                graphsByType[typeIri].push(...graphs);
              }
            );

            return {
              ...prev,
              metadata: virtualProvenance
                ? {
                    dataSourcesByIri: {
                      ...prev.metadata.dataSourcesByIri,
                      ...virtualProvenance.dataSourcesByIri,
                    },
                    schemasByIri: {
                      ...prev.metadata.schemasByIri,
                      ...virtualProvenance.schemasByIri,
                    },
                    tablesByIri: {
                      ...prev.metadata.tablesByIri,
                      ...virtualProvenance.tablesByIri,
                    },
                    columnsByIri: {
                      ...prev.metadata.columnsByIri,
                      ...virtualProvenance.columnsByIri,
                    },
                  }
                : prev.metadata,
              graphsByType,
            };
          });
        }
      );
    },
    [
      databaseId,
      fetchLocalProvenance,
      fetchVirtualProvenance,
      filteredGraphs,
      isMounted,
      provenance,
      setProvenance,
      withLoading,
    ]
  );
};
